<template>
	<section class="page page-extras" v-bind:class="{ 'has-scrolled': hasScrolled }">
		<ChalkyUiPageHero
			description="The rest of what we built &mdash; and what we&rsquo;re building next."
			title="The Kit"
			v-bind:imageUrl="$asset('image/graphic/hero-extras-c.jpg')"
		/>

		<ChalkyUtilityTabGroup v-bind:useUrl="true" v-bind:tabs="availableTabs" v-on:tab-change="Handle_OnTabChange">
			<template #after>
				<div class="actions">
					<!-- No actions -->
				</div>
			</template>

			<template v-slot="{ activeTab }">
				<div v-if="activeTab === 'tools'">
					<section class="tiles">
						<ViewUiButtonKit
							v-bind:class="{ disabled: feature.disabled }"
							v-bind:featureName="feature.disabled ? 'Coming Soon' : feature.name"
							v-bind:iconFilename="feature.icon"
							v-bind:key="feature.name"
							v-bind:route="feature.route"
							v-for="feature in features"
						/>
					</section>
				</div>

				<div v-if="activeTab === 'news'">
					<!-- Latest news articles from the community -->
					<section class="news">
						<ViewContentList
							v-bind:contentCollection="newsCollection"
							v-on:content:select="Handle_OnSelectContent"
						/>
					</section>
				</div>

				<div v-if="activeTab === 'education'">
					<!-- If we want to do tutorials / training in the future -->
					<ViewUiStateComingSoon featureName="Education" />
				</div>

				<div v-if="activeTab === 'community'">
					<!-- Public conversations maybe -->
					<ViewUiStateComingSoon featureName="Community" />
				</div>
			</template>
		</ChalkyUtilityTabGroup>
	</section>
</template>

<script lang="ts">
	import * as Mixins from '@/Mixins';
	import ChalkySticks from '@chalkysticks/sdk';
	import PageBase from '@/Core/Page.vue';
	import { Component, Prop, Watch } from 'vue-property-decorator';
	import { beforeDestroy, mounted } from '@/Utility/Decorators';

	/**
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package Page
	 * @project ChalkySticks App
	 */
	@Component({
		mixins: [Mixins.AutoScroll, Mixins.StickyScroll],
	})
	export default class PageExtras extends PageBase {
		/**
		 * @type string[]
		 */
		protected get availableTabs(): string[] {
			// return ['News',  'Education', 'Community', 'Tools'];
			return ['News', 'Tools'];
		}

		/**
		 * Current active tab slug
		 *
		 * @type string
		 */
		protected currentActiveTab: string = 'news';

		/**
		 * Collection of news content used by the News tab. Lazily fetched
		 * on first activation of that tab.
		 *
		 * @type ChalkySticks.Collection.Content
		 */
		@Prop({
			default: () => ChalkySticks.Factory.Content.collection({ limit: 12 }).news(),
		})
		public newsCollection!: ChalkySticks.Collection.Content;

		/**
		 * List of available features
		 *
		 * The true/false in disable signifies that the app build itself
		 * supports it. The getters are for whether or not the API is allowing
		 * it to be used.
		 *
		 * This allows us to toggle the feature for different versions even
		 * if a user is on an older app.
		 *
		 * @type any
		 */
		protected features: any = [
			{
				disabled: !this.$feature('kit_pad'),
				icon: 'pad.svg',
				name: 'Pad',
				route: 'PageExtrasPad',
			},
			{
				disabled: !this.$feature('kit_drills'),
				icon: 'drills.svg',
				name: 'Drills',
			},
			{
				disabled: !this.$feature('kit_killers'),
				icon: 'killers.svg',
				name: 'Killers',
			},
			{
				disabled: !this.$feature('kit_events'),
				icon: 'events.svg',
				name: 'Events',
			},
			{
				disabled: !this.$feature('kit_shotclock'),
				icon: 'shotclock.svg',
				name: 'Shot Clock',
			},
			{
				disabled: !this.$feature('kit_handicapper'),
				icon: 'handicapper.svg',
				name: 'Handicapper',
			},
			{
				disabled: !this.$feature('kit_patternracker'),
				icon: 'pattern-racker.svg',
				name: 'Pattern Racker',
			},
			{
				disabled: !this.$feature('kit_rulebooks'),
				icon: 'rulebooks.svg',
				name: 'Rulebooks',
			},
		];

		/**
		 * Configuration for sticky behavior
		 * @type IStickyScrollConfig
		 */
		protected stickyConfig: any = {
			scrollThreshold: 100,
		};

		/**
		 * Fetches the next page of news when the user scrolls near the bottom.
		 *
		 * @return Promise<void>
		 */
		protected async triggerLoadMore(): Promise<void> {
			if (this.currentActiveTab !== 'news') {
				return;
			}

			try {
				if (this.newsCollection.length > 2) {
					await this.newsCollection.nextPage(true);
				}
			} catch (error) {
				console.error('Error fetching more news:', error);
			}
		}

		/**
		 * Sync the local tab slug with the URL on mount and trigger any
		 * data fetches required for a deep-linked starting tab.
		 *
		 * @return void
		 */
		@mounted
		public setup(): void {
			this.currentActiveTab = this.$route.query.tab?.toString() || this.availableTabs[0].toLowerCase();

			if (this.currentActiveTab === 'news') {
				this.fetchNews();
			}
		}

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * Navigate to the content detail page when a news item is selected.
		 *
		 * @param ChalkySticks.Model.Content contentModel
		 * @return Promise<void>
		 */
		protected async Handle_OnSelectContent(contentModel: ChalkySticks.Model.Content): Promise<void> {
			this.$router.push({
				name: 'PageContentItem',
				params: {
					id: contentModel.getSlug(),
				},
			});
		}

		/**
		 * Lazily fetch tab data on first activation.
		 *
		 * @param string slug
		 * @return Promise<void>
		 */
		protected async Handle_OnTabChange(slug: string): Promise<void> {
			this.currentActiveTab = slug;

			if (slug === 'news') {
				this.fetchNews();
			}
		}

		// endregion: Event Handlers

		/**
		 * Fetch news collection if not recently loaded.
		 *
		 * @return void
		 */
		protected fetchNews(): void {
			if (Date.now() - this.newsCollection.timeCompleted < 1000 * 60) {
				return;
			}

			this.newsCollection.fetch();
		}
	}
</script>

<style lang="scss">
	.page-extras {
		background-color: var(--chalky-blue-4);
		display: flex;
		flex-direction: column;
		overflow: scroll !important;

		.ui-pagehero {
			filter: blur(0);
			position: sticky;
			top: -100px;
			transition: filter 0.5s ease, opacity 0.5s ease, transform 0.5s ease;
			transform-origin: top center;
			z-index: 0;

			figcaption {
				transition: opacity 0.3s ease;
			}
		}

		&.has-scrolled .ui-pagehero {
			filter: blur(10px);
			transform: translateY(-0px);

			figcaption {
				opacity: 0;
			}
		}

		.tiles {
			background-color: var(--chalky-blue-2);
			display: grid;
			gap: 1rem;
			grid-template-columns: 1fr 1fr;
			padding: 1rem;

			.tile {
			}
		}

		.news {
			background-color: var(--chalky-blue-2);
		}

		.utility-tabgroup {
			display: flex;
			flex-direction: column;
			flex-grow: 1;

			.tab-groups {
				flex-grow: 1;

				> div,
				> div > section {
					height: 100%;
				}
			}
		}

		.utility-tabselector .actions a {
			border: 0;
			color: var(--chalky-grey-2);
			font-size: 0.75rem;
			font-weight: var(--font-weight-regular);
			text-decoration: none;
			text-transform: uppercase;
			letter-spacing: 0.033rem;
		}
	}
</style>
